import { chapterAt, clampPosition, screenAt, type InterviewPosition } from "@shared/interview/chapters";
import { HomeNeeds } from "./HomeNeeds";
import { chapterTitle, fill, HOME_BOARD } from "./planBoard";
import { NO_EXECUTOR_YET, useExecutorFirstName } from "./useExecutorFirstName";
import { useOwnList } from "./useOwnList";
import { usePlannerLens } from "@/hooks/usePlannerLens";

/**
 * PLAN-HOME-1 (s228) — THE PLAN'S HOME PAGE (board 12, Home.dc.html:310-566; rule 127(6)).
 *
 *  • The progress bar is the counter's own measure — tasks ready out of all tasks (rule 119: no second score) — and
 *    "Continue where you left off" reopens the interview where the planner was (:312-332).
 *  • s232 (PR 2, board v417): the bar also counts the ONE LIST's rows — "N of M ready for {executor} — answers and things
 *    to do" (the board's `planReady` / `planTotal`: a done row is ready, a skipped row leaves the count).
 *  • "What your executor still needs", full width (HomeNeeds).
 *
 * Not built in this block, reported s228: the search box (:386-400), "Things only you can do" (Reade, ruling 10),
 * the plan-complete banner and its celebration (:333-351, :27-58), and the header's "last changed" date (the plan
 * keeps no such date to read).
 */
export function PlanHome({
  clientId,
  counter,
  position,
  onContinue,
  onOpen,
}: {
  clientId: string;
  counter: { loaded: boolean; total: number; ready: number };
  /** Where the planner is in the interview (saved), for "Continue where you left off". */
  position: InterviewPosition | null;
  onContinue: () => void;
  onOpen: (screenKey: string) => void;
}) {
  const executor = useExecutorFirstName(clientId);
  const at = clampPosition(position);
  const chapter = chapterAt(at);
  const screen = screenAt(at);
  const where = chapter && screen ? `${chapterTitle(chapter.number)} · ${screen.crumb}` : "";
  const { data: own } = useOwnList(clientId);
  // s233 (D-233.55, Reade: "yes"): the plan bar counts ANSWERS, the unit of the list under it — (answers given + hand-overs
  // done) of (answers asked + hand-overs not skipped). "Answers asked" is ONE definition: the planner lens's `askedTotal`
  // (shared/planner-lens.ts, evaluatePlannerLensRecords), missing = its `recordTotal` — the very rows the list shows — plus
  // the home's open answer rows. So the bar's remaining is always the list's N + M. The executor bar keeps tasks (D-233.30).
  const { data: lens } = usePlannerLens(clientId);
  const asked = lens?.askedTotal ?? 0;
  const given = asked - (lens?.recordTotal ?? 0);
  const questions = asked + (Array.isArray(own?.answers) ? own.answers.length : 0);
  const total = questions + (own?.counts?.live ?? 0);
  const ready = given + (own?.counts?.done ?? 0);
  const pct = total > 0 ? Math.round((ready / total) * 100) : 0;
  const execPct = counter.total > 0 ? Math.round((counter.ready / counter.total) * 100) : 0;
  const who = executor || NO_EXECUTOR_YET;

  return (
    // s228 (Reade, staging walk item 1): the page sits against the sidebar and uses the width there is — no narrow
    // centred column leaving a gap after the sidebar.
    <div className="w-full px-4 pb-12 pt-7 sm:px-8" data-testid="plan-home">
      <div className="flex w-full flex-col gap-5">
        {counter.loaded && !!lens && (
          <div className="flex flex-wrap items-center gap-6 rounded-[12px] border border-warm-stone bg-warm-white px-6 py-[18px]" data-testid="home-progress">
            <div className="flex min-w-0 flex-grow flex-col gap-2.5">
              <div className="flex flex-wrap items-baseline gap-2.5">
                <span className="font-serif text-[26px] text-sage-800" data-testid="home-progress-numbers">{ready} of {total}</span>
                <span className="text-[15px]" data-testid="home-progress-word">{fill(HOME_BOARD.ownList.barWord, { executor: who })}</span>
              </div>
              {/* s233 (D-233.13): the plan bar's line — its things, its questions, its things only you can do, done. */}
              <span className="-mt-1 text-[13px] leading-normal text-warm-muted" data-testid="home-progress-sub">
                {fill(HOME_BOARD.ownList.planSub, { total, questions, dos: own?.counts?.live ?? 0, done: ready, executor: who })}
              </span>
              <div className="h-3 overflow-hidden rounded-full bg-warm-stone-light">
                <div className="h-3 rounded-full bg-sage-600 transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="home-progress-bar" />
              </div>
              {/* s233 (D-233.13; D-233.30): the executor bar — the counter alone (tasks ready of all tasks); the hand-overs
                  count only in the plan bar above. */}
              <div className="flex flex-wrap items-baseline gap-2.5 pt-1.5">
                <span className="font-serif text-[26px] text-sage-800" data-testid="home-exec-numbers">{counter.ready} of {counter.total}</span>
                <span className="text-[15px]" data-testid="home-exec-word">{fill(HOME_BOARD.ownList.execWord, { executor: who })}</span>
              </div>
              <span className="-mt-1 text-[13px] leading-normal text-warm-muted" data-testid="home-exec-sub">
                {fill(HOME_BOARD.ownList.execSub, { executor: who })}
              </span>
              <div className="h-3 overflow-hidden rounded-full bg-warm-stone-light">
                <div className="h-3 rounded-full bg-[#B08A4E] transition-[width] duration-500" style={{ width: `${execPct}%` }} data-testid="home-exec-bar" />
              </div>
            </div>
            <button type="button" onClick={onContinue}
              className="flex min-h-14 shrink-0 flex-col justify-center gap-0.5 rounded-[12px] bg-sage-800 px-5 text-left text-warm-white hover:bg-sage-900"
              data-testid="home-continue">
              <span className="text-[15px]">Continue where you left off ›</span>
              {/* s233 (D-233.37 point 2): the line is cream, #F1E7C3 (board 12 v433). */}
              {where && <span className="text-[13px] text-[#F1E7C3]" data-testid="home-continue-where">{where}</span>}
            </button>
          </div>
        )}
        <HomeNeeds clientId={clientId} onOpen={onOpen} />
      </div>
    </div>
  );
}
